<template>
  <div class="wholesale-page">
    <section class="wholesale-hero" aria-labelledby="wholesale-title">
      <NuxtImg
        class="wholesale-hero__image"
        src="/k/kendrick-paris/store/kendrick-store-wholesale-display.jpg"
        alt="Kendrick Paris shoes and accessories presented in a boutique display"
        width="1789"
        height="1223"
        sizes="sm:100vw md:100vw lg:100vw"
        preload
      />
      <div class="wholesale-hero__inner">
        <p class="wholesale-kicker">Kendrick Paris — Wholesale</p>
        <h1 id="wholesale-title">The line sheet.</h1>
        <p>
          Three silhouettes, designed in Paris and made in Italy, for a small circle of boutiques
          and showrooms.
        </p>
        <div class="wholesale-hero__actions" aria-label="Wholesale actions">
          <a class="wholesale-button wholesale-button--light" href="#request">Request the line sheet</a>
          <a class="wholesale-button wholesale-button--outline" href="#order-request">Start an order</a>
        </div>
      </div>
    </section>

    <section class="wholesale-strip" aria-label="Buyer context">
      <div v-for="item in buyerSignals" :key="item.label">
        <span>{{ item.label }}</span>
        <strong>{{ item.value }}</strong>
      </div>
    </section>

    <section id="request" class="wholesale-section wholesale-section--request" aria-labelledby="request-title">
      <div class="wholesale-section__head">
        <p class="wholesale-kicker">By invitation</p>
        <h2 id="request-title">Receive the line sheet.</h2>
        <p>Leave your details. The line sheet, prices and delivery windows reach you directly, and Kendrick follows up personally.</p>
      </div>
      <WholesaleLineSheetRequestForm />
    </section>

    <section id="line-sheet" class="wholesale-section wholesale-section--sheet" aria-labelledby="line-sheet-title">
      <div class="wholesale-section__head">
        <p class="wholesale-kicker">The edit</p>
        <h2 id="line-sheet-title">A short edit.</h2>
        <p>
          Everything a buyer needs to judge the silhouette, the price band and a first order,
          before an appointment.
        </p>
      </div>

      <div class="line-sheet-grid">
        <article v-for="product in products" :key="product.sku" class="product-sheet">
          <div class="product-sheet__media">
            <NuxtImg
              :src="product.image"
              :alt="product.alt"
              width="900"
              height="1100"
              sizes="sm:100vw md:45vw lg:30vw"
              loading="lazy"
            />
          </div>
          <div class="product-sheet__body">
            <div>
              <p class="product-sheet__sku">{{ product.sku }}</p>
              <h3>{{ product.name }}</h3>
              <p>{{ product.description }}</p>
            </div>
            <dl class="product-specs">
              <div>
                <dt>Retail reference</dt>
                <dd>{{ product.retailPrice }}</dd>
              </div>
              <div>
                <dt>Sizes</dt>
                <dd>{{ product.sizeRange }}</dd>
              </div>
              <div>
                <dt>Making</dt>
                <dd>{{ product.making }}</dd>
              </div>
              <div>
                <dt>Pilot logic</dt>
                <dd>{{ product.pilot }}</dd>
              </div>
            </dl>
          </div>
        </article>
      </div>
    </section>

    <section class="wholesale-section wholesale-section--proof" aria-labelledby="proof-title">
      <div class="wholesale-proof__copy">
        <p class="wholesale-kicker">Buyer proof</p>
        <h2 id="proof-title">Already readable on camera and in press</h2>
        <p>
          Kendrick has a real visual memory: French TV appearances, magazine context, black and red
          silhouettes that read immediately, and product details that support a premium sale.
        </p>
        <NuxtLink class="wholesale-text-link" to="/press">
          View press archive
          <ExternalLink class="wholesale-text-link__icon" aria-hidden="true" />
        </NuxtLink>
      </div>

      <div class="wholesale-proof__media">
        <NuxtImg
          src="/medias/press/palacescope/palacescope_kendrick.jpg"
          alt="Kendrick Paris ankle boots in PalaceScope editorial context"
          width="980"
          height="640"
          sizes="sm:100vw md:48vw lg:44vw"
          loading="eager"
        />
      </div>
    </section>

    <section id="order-request" class="wholesale-section wholesale-section--order" aria-labelledby="order-title">
      <div class="wholesale-section__head">
        <p class="wholesale-kicker">Order request</p>
        <h2 id="order-title">Build a first size run</h2>
        <p>
          This is not an automatic checkout. It captures a professional order intention so Kendrick
          can confirm wholesale terms, delivery window, production feasibility and payment structure.
        </p>
      </div>

      <form class="order-form" @submit.prevent="submitLead">
        <div class="form-honeypot" aria-hidden="true">
          <label for="wholesale-company">Company</label>
          <input
            id="wholesale-company"
            v-model="form.company"
            name="company"
            type="text"
            tabindex="-1"
            autocomplete="off"
          >
        </div>

        <section class="order-panel" aria-labelledby="buyer-details-title">
          <div class="order-panel__head">
            <h3 id="buyer-details-title">Buyer details</h3>
            <p>Used only to qualify the request and answer directly.</p>
          </div>

          <div class="form-grid">
            <label class="field">
              <span>Store / company *</span>
              <div class="field-control">
                <input v-model="form.boutiqueName" required type="text" autocomplete="organization">
                <button v-if="form.boutiqueName" type="button" aria-label="Clear store name" @click="clearTextField('boutiqueName')">
                  <X aria-hidden="true" />
                </button>
              </div>
            </label>
            <label class="field">
              <span>City *</span>
              <div class="field-control">
                <input v-model="form.city" required type="text" autocomplete="address-level2">
                <button v-if="form.city" type="button" aria-label="Clear city" @click="clearTextField('city')">
                  <X aria-hidden="true" />
                </button>
              </div>
            </label>
            <label class="field">
              <span>Contact name</span>
              <div class="field-control">
                <input v-model="form.contactName" type="text" autocomplete="name">
                <button v-if="form.contactName" type="button" aria-label="Clear contact name" @click="clearTextField('contactName')">
                  <X aria-hidden="true" />
                </button>
              </div>
            </label>
            <label class="field">
              <span>Email *</span>
              <div class="field-control">
                <input v-model="form.email" required type="email" autocomplete="email">
                <button v-if="form.email" type="button" aria-label="Clear email" @click="clearTextField('email')">
                  <X aria-hidden="true" />
                </button>
              </div>
            </label>
            <label class="field">
              <span>Website</span>
              <div class="field-control">
                <input v-model="form.website" type="url" inputmode="url" placeholder="https://">
                <button v-if="form.website" type="button" aria-label="Clear website" @click="clearTextField('website')">
                  <X aria-hidden="true" />
                </button>
              </div>
            </label>
            <label class="field">
              <span>Phone</span>
              <div class="field-control">
                <input v-model="form.phone" type="tel" autocomplete="tel">
                <button v-if="form.phone" type="button" aria-label="Clear phone" @click="clearTextField('phone')">
                  <X aria-hidden="true" />
                </button>
              </div>
            </label>
          </div>
        </section>

        <section class="order-panel" aria-labelledby="size-run-title">
          <div class="order-panel__head order-panel__head--split">
            <div>
              <h3 id="size-run-title">Indicative size run</h3>
              <p>Use quantities only where there is real buyer intent.</p>
            </div>
            <div class="total-pairs">
              <span>Total</span>
              <strong>{{ totalPairs }} pairs</strong>
            </div>
          </div>

          <div class="size-matrix">
            <article v-for="product in products" :key="`matrix-${product.sku}`" class="size-matrix__product">
              <div class="size-matrix__product-head">
                <strong>{{ product.shortName }}</strong>
                <span>{{ product.retailPrice }}</span>
              </div>
              <div class="size-row">
                <label v-for="size in sizes" :key="`${product.sku}-${size}`" class="size-cell">
                  <span>{{ size }}</span>
                  <div class="quantity-control">
                    <input
                      v-model.number="quantities[product.sku][size]"
                      type="number"
                      min="0"
                      max="99"
                      inputmode="numeric"
                      :aria-label="`${product.shortName} size ${size} quantity`"
                    >
                    <button
                      v-if="quantities[product.sku][size]"
                      type="button"
                      :aria-label="`Clear ${product.shortName} size ${size}`"
                      @click="clearQuantity(product.sku, size)"
                    >
                      <X aria-hidden="true" />
                    </button>
                  </div>
                </label>
              </div>
            </article>
          </div>
        </section>

        <section class="order-panel" aria-labelledby="request-context-title">
          <div class="order-panel__head">
            <h3 id="request-context-title">Request context</h3>
            <p>Helps decide whether this should be a line sheet, pilot order or appointment.</p>
          </div>

          <div class="form-grid">
            <label class="field">
              <span>Request type</span>
              <select v-model="form.requestType">
                <option value="line_sheet">Line sheet review</option>
                <option value="pilot_request">Pilot discussion</option>
                <option value="order_request">Order request</option>
              </select>
            </label>
            <label class="field">
              <span>Target delivery</span>
              <div class="field-control">
                <input v-model="form.targetDelivery" type="text" placeholder="Example: October market / holiday window">
                <button v-if="form.targetDelivery" type="button" aria-label="Clear target delivery" @click="clearTextField('targetDelivery')">
                  <X aria-hidden="true" />
                </button>
              </div>
            </label>
          </div>

          <label class="field">
            <span>Store profile / brands carried</span>
            <div class="field-control field-control--textarea">
              <textarea v-model="form.brandsCarried" rows="3" placeholder="Example: designer ready-to-wear, eveningwear, occasion shoes, French labels." />
              <button v-if="form.brandsCarried" type="button" aria-label="Clear store profile" @click="clearTextField('brandsCarried')">
                <X aria-hidden="true" />
              </button>
            </div>
          </label>

          <label class="field">
            <span>Message</span>
            <div class="field-control field-control--textarea">
              <textarea v-model="form.message" rows="4" placeholder="Tell us whether you want a line sheet, a short appointment, or a first order discussion." />
              <button v-if="form.message" type="button" aria-label="Clear message" @click="clearTextField('message')">
                <X aria-hidden="true" />
              </button>
            </div>
          </label>

          <div class="form-checks">
            <label>
              <input v-model="form.requestedLineSheet" type="checkbox">
              <span>Send me the full line sheet and professional terms.</span>
            </label>
            <label>
              <input v-model="form.requestedBuyerProof" type="checkbox">
              <span>Send buyer proof and press/TV context if the fit is right.</span>
            </label>
            <label>
              <input v-model="form.newsletterOptIn" type="checkbox">
              <span>Keep me informed about Kendrick professional releases.</span>
            </label>
          </div>
        </section>

        <p v-if="submitError" class="form-alert form-alert--error" role="alert">{{ submitError }}</p>
        <p v-if="submitState === 'success'" class="form-alert form-alert--success" role="status">
          Request received. Kendrick will review the fit before sending terms or confirming any order.
        </p>

        <button class="wholesale-submit" type="submit" :disabled="submitState === 'submitting'">
          <Send class="wholesale-submit__icon" aria-hidden="true" />
          {{ submitState === 'submitting' ? 'Sending request' : 'Send wholesale request' }}
        </button>
      </form>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import { ExternalLink, Send, X } from 'lucide-vue-next'
import { useBehavioralBotDetection } from '~/composables/useBehavioralBotDetection'

definePageMeta({
  layout: 'editorial',
  headerOnDark: true,
  nuxtI18n: false
})

type RequestType = 'line_sheet' | 'pilot_request' | 'order_request'
type TextField = 'boutiqueName' | 'city' | 'website' | 'contactName' | 'email' | 'phone' | 'targetDelivery' | 'brandsCarried' | 'message'
type ProductSku = 'ALIGN-110-BLACK' | 'AXIS-110-BLACK' | 'EDGE-110-BLACK'
type Size = '35' | '36' | '37' | '38' | '39' | '40' | '41' | '42'
type QuantityMatrix = Record<ProductSku, Record<Size, number | ''>>

const route = useRoute()
const signalsKit = useSignalsKit()
const { initialize, cleanup, getBotData } = useBehavioralBotDetection()

const sizes: Size[] = ['35', '36', '37', '38', '39', '40', '41', '42']

const products: Array<{
  sku: ProductSku
  name: string
  shortName: string
  retailPrice: string
  sizeRange: string
  making: string
  pilot: string
  image: string
  alt: string
  description: string
}> = [
  {
    sku: 'AXIS-110-BLACK',
    name: 'AXIS 110 black boot',
    shortName: 'AXIS 110',
    retailPrice: 'EUR 980 retail reference',
    sizeRange: '35-42 EU',
    making: 'Designed in Paris, crafted in Italy',
    pilot: 'Statement boot for clients who already own the ankle boot',
    image: '/product/boots/kendrick-boots-black-1.jpg',
    alt: 'Black Kendrick AXIS 110 boot, studio view',
    description: 'The long line: a black boot with a clean shaft and a 110 mm heel, for stores that want a stronger winter silhouette.'
  },
  {
    sku: 'ALIGN-110-BLACK',
    name: 'ALIGN 110 black ankle boot',
    shortName: 'ALIGN 110',
    retailPrice: 'EUR 680 retail reference',
    sizeRange: '35-42 EU',
    making: 'Designed in Paris, crafted in Italy',
    pilot: '6-18 pair first run, depending on store fit',
    image: '/product/ankle-boots/kendrick-ankle-boots-black-1.jpg',
    alt: 'Black Kendrick ALIGN 110 ankle boot, studio view',
    description: 'The anchor of the house: a sharp black ankle boot built for dinner, hotel bar, private event and Paris night-out dressing.'
  },
  {
    sku: 'EDGE-110-BLACK',
    name: 'EDGE 110 black pump',
    shortName: 'EDGE 110',
    retailPrice: 'EUR 450 retail reference',
    sizeRange: '35-42 EU',
    making: 'Designed in Paris, crafted in Italy',
    pilot: 'Formal complement for appointment or eveningwear clients',
    image: '/product/pumps/kendrick-pumps-black-1.jpg',
    alt: 'Black Kendrick EDGE 110 pump, studio view',
    description: 'A formal stiletto alternative for stores that need a cleaner evening line beside the ankle boot.'
  }
]

const buyerSignals = [
  { label: 'First edit', value: '3 silhouettes' },
  { label: 'Positioning', value: 'Paris / Italy' },
  { label: 'Retail band', value: 'EUR 450-980' },
  { label: 'Status', value: 'pilot only' }
]

const emptyQuantities = (): QuantityMatrix => products.reduce((matrix, product) => {
  matrix[product.sku] = sizes.reduce((row, size) => {
    row[size] = ''
    return row
  }, {} as Record<Size, number | ''>)
  return matrix
}, {} as QuantityMatrix)

const initialForm = {
  boutiqueName: '',
  city: '',
  website: '',
  contactName: '',
  email: '',
  phone: '',
  targetDelivery: '',
  brandsCarried: '',
  message: '',
  requestType: 'line_sheet' as RequestType,
  requestedLineSheet: true,
  requestedBuyerProof: true,
  newsletterOptIn: false,
  company: ''
}

const form = reactive({ ...initialForm })
const quantities = reactive<QuantityMatrix>(emptyQuantities())
const submitState = ref<'idle' | 'submitting' | 'success' | 'error'>('idle')
const submitError = ref('')

const totalPairs = computed(() => products.reduce((total, product) => {
  return total + sizes.reduce((productTotal, size) => {
    const value = Number(quantities[product.sku][size] || 0)
    return productTotal + (Number.isFinite(value) && value > 0 ? value : 0)
  }, 0)
}, 0))

const orderProducts = computed(() => products.map(product => {
  const sizeQuantities = sizes.reduce((row, size) => {
    const value = Number(quantities[product.sku][size] || 0)
    if (Number.isFinite(value) && value > 0) {
      row[size] = Math.min(Math.floor(value), 99)
    }
    return row
  }, {} as Partial<Record<Size, number>>)

  return {
    sku: product.sku,
    name: product.name,
    retail_price: product.retailPrice,
    quantities: sizeQuantities,
    total_pairs: Object.values(sizeQuantities).reduce((sum, value) => sum + Number(value || 0), 0)
  }
}).filter(product => product.total_pairs > 0))

const clearTextField = (field: TextField) => {
  form[field] = ''
}

const clearQuantity = (sku: ProductSku, size: Size) => {
  quantities[sku][size] = ''
}

const resetQuantities = () => {
  products.forEach((product) => {
    sizes.forEach((size) => {
      quantities[product.sku][size] = ''
    })
  })
}

const submitLead = async () => {
  submitError.value = ''

  if (!form.boutiqueName.trim() || !form.city.trim() || !form.email.trim()) {
    submitState.value = 'error'
    submitError.value = 'Store/company, city, and email are required.'
    return
  }

  submitState.value = 'submitting'

  const requestType: RequestType = totalPairs.value > 0 ? 'order_request' : form.requestType
  const orderDetails = {
    channel: 'site_wholesale_studio',
    requested_buyer_proof: form.requestedBuyerProof,
    target_delivery: form.targetDelivery.trim() || null,
    total_pairs: totalPairs.value,
    products: orderProducts.value
  }

  try {
    const response = await $fetch<{ success?: boolean, data?: { id?: number } }>('/api/b2b-lead', {
      method: 'POST',
      body: {
        boutiqueName: form.boutiqueName,
        city: form.city,
        website: form.website,
        contactName: form.contactName,
        email: form.email,
        phone: form.phone,
        brandsCarried: form.brandsCarried,
        interest: requestType === 'order_request' ? 'Wholesale order request' : 'Wholesale line sheet',
        message: form.message,
        requestType,
        orderDetails,
        requestedLineSheet: form.requestedLineSheet,
        newsletterOptIn: form.newsletterOptIn,
        company: form.company,
        sourcePath: route.fullPath,
        botData: getBotData('wholesale_order_request')
      }
    })
    signalsKit.trackFormGoal({
      formType: 'b2b_lead',
      formLocation: 'wholesale_order_page',
      requestType,
      locale: 'en',
      leadId: response?.data?.id || null,
      properties: {
        page_path: route.path,
        route_full_path: route.fullPath,
        total_pairs: totalPairs.value,
        requested_line_sheet: Boolean(form.requestedLineSheet),
        newsletter_opt_in: Boolean(form.newsletterOptIn),
        has_phone: Boolean(form.phone.trim()),
        has_website: Boolean(form.website.trim()),
        has_message: Boolean(form.message.trim()),
        b2b_lead_id: response?.data?.id || null
      }
    })

    Object.assign(form, initialForm)
    resetQuantities()
    submitState.value = 'success'
  } catch (error: any) {
    submitState.value = 'error'
    submitError.value = error?.data?.statusMessage || error?.statusMessage || 'The request could not be sent. Please try again.'
  }
}

onMounted(() => {
  void initialize()
})

onUnmounted(() => {
  cleanup()
})

const pageTitle = 'Kendrick Paris wholesale line sheet'
const pageDescription = 'B2B line sheet and wholesale order request for Kendrick Paris buyers, showrooms and selected retail partners.'

useHead({
  htmlAttrs: { lang: 'en-US' },
  title: pageTitle,
  meta: [
    { name: 'description', content: pageDescription },
    { property: 'og:title', content: pageTitle },
    { property: 'og:description', content: pageDescription },
    { property: 'og:type', content: 'website' },
    { property: 'og:image', content: 'https://kendrick.paris/k/kendrick-paris/store/kendrick-store-wholesale-display.jpg' }
  ],
  link: [
    { rel: 'canonical', href: 'https://kendrick.paris/wholesales' }
  ]
})
</script>

<style scoped>
.wholesale-page {
  --ink: #141210;
  --muted: #67605a;
  --paper: #f6f1ea;
  --line: rgba(26, 23, 20, 0.14);
  --red: #8f2020;
  --green: #31483d;
  --serif: 'Playfair Display', Georgia, serif;
  --sans: Inter, 'Helvetica Neue', Arial, sans-serif;

  background: var(--paper);
  color: var(--ink);
  font-family: var(--sans);
}

.wholesale-hero {
  position: relative;
  overflow: hidden;
  min-height: 100svh;
  display: flex;
  align-items: flex-end;
  padding: clamp(7rem, 12vw, 11rem) clamp(1.25rem, 5vw, 5rem) clamp(3rem, 7vw, 5rem);
  background: #141210;
  color: #fffaf2;
}

.wholesale-hero::after {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(180deg, rgba(9, 8, 7, 0.55) 0%, rgba(9, 8, 7, 0.1) 40%, rgba(9, 8, 7, 0.82) 100%);
  content: '';
}

.wholesale-hero__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media (max-width: 899px) {
  .wholesale-hero__image {
    width: 116%;
    max-width: none;
    animation: wholesale-hero-pan 18s ease-in-out infinite alternate;
  }
}

@media (prefers-reduced-motion: reduce) {
  .wholesale-hero__image {
    animation: none;
  }
}

@keyframes wholesale-hero-pan {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(-14%);
  }
}

.wholesale-hero__inner {
  position: relative;
  z-index: 2;
  max-width: 54rem;
}

.wholesale-kicker {
  margin: 0 0 0.9rem;
  color: currentColor;
  font-size: 0.62rem;
  font-weight: 400;
  letter-spacing: 0.36em;
  line-height: 1.35;
  opacity: 0.6;
  text-transform: uppercase;
}

.wholesale-hero h1,
.wholesale-section__head h2,
.wholesale-proof__copy h2 {
  margin: 0;
  font-family: var(--serif);
  font-weight: 500;
  letter-spacing: 0;
}

.wholesale-hero h1 {
  max-width: 12ch;
  font-size: clamp(3.8rem, 9vw, 8rem);
  line-height: 0.95;
}

.wholesale-hero p {
  max-width: 43rem;
  margin: 1.6rem 0 0;
  color: rgba(255, 250, 242, 0.78);
  font-size: clamp(0.95rem, 1.3vw, 1.1rem);
  line-height: 1.8;
}

.wholesale-hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 2rem;
}

.wholesale-button,
.wholesale-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 3rem;
  gap: 0.55rem;
  border-radius: 999px;
  padding: 0.85rem 2rem;
  white-space: nowrap;
  font-size: 0.7rem;
  font-weight: 400;
  letter-spacing: 0.28em;
  line-height: 1.2;
  text-align: center;
  text-decoration: none;
  text-transform: uppercase;
}

.wholesale-button__icon,
.wholesale-submit__icon {
  width: 1rem;
  height: 1rem;
  flex: 0 0 auto;
}

.wholesale-button--light {
  border: 1px solid #fffaf2;
  background: #fffaf2;
  color: var(--ink);
}

.wholesale-button--outline {
  border: 1px solid rgba(255, 250, 242, 0.46);
  color: #fffaf2;
}

.wholesale-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
  border-block: 1px solid var(--line);
  background: transparent;
}

.wholesale-strip div {
  min-height: 6rem;
  display: grid;
  align-content: center;
  gap: 0.3rem;
  border-right: 1px solid var(--line);
  padding: 1rem clamp(1rem, 3vw, 2rem);
}

.wholesale-strip span {
  color: var(--muted);
  font-size: 0.6rem;
  font-weight: 400;
  letter-spacing: 0.3em;
  text-transform: uppercase;
}

.wholesale-strip strong {
  font-family: var(--serif);
  font-size: 1.25rem;
  font-weight: 400;
}

.wholesale-section--request {
  border-bottom: 1px solid var(--line);
}

.wholesale-section {
  padding: clamp(3.75rem, 8vw, 6.5rem) clamp(1.25rem, 5vw, 5rem);
}

.wholesale-section__head {
  max-width: 50rem;
  margin-bottom: 2rem;
}

.wholesale-section__head h2,
.wholesale-proof__copy h2 {
  max-width: 14ch;
  margin-bottom: 1.5rem;
  font-size: clamp(2.45rem, 5vw, 4.4rem);
  line-height: 1.08;
}

.wholesale-section__head p,
.wholesale-proof__copy p,
.order-panel__head p,
.product-sheet p,
.product-specs,
.form-alert {
  color: var(--muted);
  font-size: 1rem;
  line-height: 1.7;
}

.line-sheet-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 24rem), 1fr));
  gap: 1rem;
}

.product-sheet {
  display: grid;
  grid-template-rows: auto 1fr;
  min-height: 100%;
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: #fff;
}

.product-sheet__media {
  aspect-ratio: 4 / 5;
  overflow: hidden;
  background: #fff;
}

.product-sheet__media img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.product-sheet__body {
  display: grid;
  align-content: start;
  gap: 1.2rem;
  padding: clamp(1rem, 2.5vw, 1.4rem);
}

.product-sheet__sku {
  margin: 0 0 0.45rem;
  color: var(--red);
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.product-sheet h3,
.order-panel__head h3 {
  margin: 0;
  font-size: 1.2rem;
  letter-spacing: 0;
}

.product-sheet p {
  margin: 0.7rem 0 0;
}

.product-specs {
  display: grid;
  gap: 0;
  border-top: 1px solid var(--line);
}

.product-specs div {
  display: grid;
  grid-template-columns: minmax(7rem, 0.45fr) minmax(0, 1fr);
  gap: 1rem;
  border-bottom: 1px solid var(--line);
  padding: 0.75rem 0;
}

.product-specs dt {
  color: #332e29;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.product-specs dd {
  margin: 0;
}

.wholesale-section--proof {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 2rem;
  background: #141210;
  color: #fffaf2;
}

@media (min-width: 900px) {
  .wholesale-section--proof {
    grid-template-columns: minmax(0, 0.72fr) minmax(360px, 0.84fr);
    align-items: center;
  }
}

.wholesale-proof__copy p {
  color: rgba(255, 250, 242, 0.72);
}

.wholesale-text-link {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin-top: 1rem;
  color: #fffaf2;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-decoration: underline;
  text-transform: uppercase;
  text-underline-offset: 0.22em;
}

.wholesale-text-link__icon {
  width: 0.95rem;
  height: 0.95rem;
}

.wholesale-proof__media {
  overflow: hidden;
  border: 1px solid rgba(255, 250, 242, 0.18);
  border-radius: 6px;
}

.wholesale-proof__media img {
  display: block;
  width: 100%;
  aspect-ratio: 7 / 5;
  height: auto;
  object-fit: cover;
}

.wholesale-section--order {
  background: #f0ece5;
}

.order-form {
  display: grid;
  gap: 1rem;
}

.form-honeypot {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

.order-panel {
  border: 1px solid var(--line);
  border-radius: 6px;
  background: #fff;
  padding: clamp(1rem, 2.5vw, 1.4rem);
}

.order-panel__head {
  margin-bottom: 1rem;
}

.order-panel__head p {
  margin: 0.35rem 0 0;
}

.order-panel__head--split {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.total-pairs {
  display: grid;
  min-width: 8rem;
  border: 1px solid var(--line);
  border-radius: 4px;
  padding: 0.75rem 0.9rem;
}

.total-pairs span {
  color: var(--muted);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.total-pairs strong {
  margin-top: 0.2rem;
  font-size: 1.05rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 15rem), 1fr));
  gap: 0.85rem;
}

.field {
  display: grid;
  gap: 0.4rem;
  margin-bottom: 0.9rem;
}

.field > span {
  color: #403a34;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.field-control,
.quantity-control {
  position: relative;
}

.field input,
.field select,
.field textarea,
.quantity-control input {
  width: 100%;
  min-width: 0;
  border: 1px solid #cfc7bd;
  border-radius: 4px;
  background: #fbf8f3;
  color: var(--ink);
  font: inherit;
  font-size: 1rem;
  line-height: 1.35;
  outline: none;
}

.field input,
.field select {
  min-height: 2.85rem;
  padding: 0.76rem 2.35rem 0.76rem 0.78rem;
}

.field textarea {
  resize: vertical;
  padding: 0.82rem 2.35rem 0.82rem 0.78rem;
}

.field input:focus,
.field select:focus,
.field textarea:focus,
.quantity-control input:focus {
  border-color: var(--red);
  box-shadow: 0 0 0 3px rgba(143, 32, 32, 0.1);
}

.field-control button,
.quantity-control button {
  position: absolute;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  background: rgba(20, 18, 16, 0.08);
  color: var(--ink);
  cursor: pointer;
}

.field-control button {
  top: 0.65rem;
  right: 0.65rem;
  width: 1.55rem;
  height: 1.55rem;
}

.field-control--textarea button {
  top: 0.75rem;
}

.field-control svg,
.quantity-control svg {
  width: 0.92rem;
  height: 0.92rem;
}

.size-matrix {
  display: grid;
  gap: 1rem;
}

.size-matrix__product {
  border: 1px solid #e3ddd6;
  border-radius: 6px;
  padding: 1rem;
}

.size-matrix__product-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.7rem;
  margin-bottom: 0.85rem;
}

.size-matrix__product-head span {
  color: var(--muted);
  font-size: 0.85rem;
}

.size-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.55rem;
}

@media (min-width: 760px) {
  .size-row {
    grid-template-columns: repeat(8, minmax(0, 1fr));
  }
}

.size-cell {
  display: grid;
  gap: 0.28rem;
}

.size-cell > span {
  color: var(--muted);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.13em;
  text-align: center;
  text-transform: uppercase;
}

.quantity-control input {
  height: 2.8rem;
  padding: 0.62rem 1.65rem 0.62rem 0.55rem;
  text-align: center;
}

.quantity-control button {
  top: 0.65rem;
  right: 0.35rem;
  width: 1.45rem;
  height: 1.45rem;
}

.form-checks {
  display: grid;
  gap: 0.65rem;
  margin-top: 0.8rem;
}

.form-checks label {
  display: grid;
  grid-template-columns: 1.2rem minmax(0, 1fr);
  gap: 0.7rem;
  align-items: start;
}

.form-checks input {
  width: 1.08rem;
  height: 1.08rem;
  margin-top: 0.2rem;
  accent-color: var(--ink);
}

.form-checks span {
  color: var(--muted);
  line-height: 1.5;
}

.form-alert {
  border-radius: 4px;
  margin: 0;
  padding: 0.85rem 0.95rem;
}

.form-alert--error {
  border: 1px solid #e3b3ad;
  background: #fff4f1;
  color: #7b211a;
}

.form-alert--success {
  border: 1px solid #b9d7c8;
  background: #f0fbf4;
  color: #214c39;
}

.wholesale-submit {
  width: 100%;
  border: 1px solid var(--ink);
  background: var(--ink);
  color: #fffaf2;
  cursor: pointer;
}

.wholesale-submit:hover:not(:disabled) {
  border-color: var(--red);
  background: var(--red);
}

.wholesale-submit:disabled {
  cursor: wait;
  opacity: 0.7;
}

@media (max-width: 520px) {
  .wholesale-button,
  .wholesale-submit {
    width: 100%;
  }

  .product-specs div {
    grid-template-columns: 1fr;
    gap: 0.35rem;
  }
}
</style>
